import { useEffect, useRef, useState } from 'react';

export default function DesignPhilosophy() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const principles = [
    {
      icon: 'ri-user-heart-line',
      title: 'User Centered',
      oneLiner: 'Every decision starts with empathy and real user needs.',
      fullDesc: 'Every design decision I make begins with the user. I take time to research, build personas, and validate ideas through testing. For me, understanding real needs, pain points, and behaviors is the key to creating experiences that truly matter.',
      image: 'https://public.readdy.ai/ai/img_res/7b5f48297895b99b5fcbcd87e09ab39b.jpg',
      position: 'top-left'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Simple & Intuitive',
      oneLiner: 'I design for clarity, not complexity.',
      fullDesc: 'I believe great design should feel effortless. My focus is on stripping away the unnecessary and highlighting what\'s essential. By simplifying workflows and building clear visual hierarchies, I help users achieve their goals without confusion or frustration.',
      image: 'https://readdy.ai/api/search-image?query=minimalist%20clean%20interface%20design%20simple%20elegant%20user%20interface%20wireframe%20sketches%20whiteboard%20brainstorming%20clarity%20simplicity%20organized%20workspace%20design%20thinking%20process%20clean%20aesthetic%20modern%20professional%20emerald%20green%20tones%20fresh%20accents%20natural%20light&width=800&height=500&seq=principle-simple-intuitive-emerald-new&orientation=landscape',
      position: 'top-right'
    },
    {
      icon: 'ri-eye-line',
      title: 'Visually Engaging',
      oneLiner: 'Interfaces that balance beauty with usability.',
      fullDesc: 'Aesthetics play a big role in usability. I design interfaces that are not only visually appealing but also purposeful. Thoughtful use of color, typography, spacing, and motion allows me to craft experiences that delight while staying accessible and consistent with the brand.',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20colorful%20user%20interface%20design%20vibrant%20emerald%20green%20and%20teal%20colors%20modern%20aesthetic%20visual%20design%20elements%20typography%20color%20palette%20creative%20workspace%20design%20studio%20artistic%20professional%20high%20quality%20polished%20interface%20gradient%20accents%20fresh%20minimalist&width=800&height=500&seq=principle-visual-engaging-emerald-new&orientation=landscape',
      position: 'bottom-left'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Iterative Process',
      oneLiner: 'Always evolving through feedback and refinement.',
      fullDesc: 'I see design as an evolving process. I actively seek feedback, analyze results, and refine solutions. Each iteration brings me closer to the best possible experience, ensuring the product grows alongside user needs and business goals.',
      image: 'https://readdy.ai/api/search-image?query=design%20iteration%20process%20feedback%20loop%20sticky%20notes%20prototypes%20sketches%20agile%20workflow%20team%20collaboration%20whiteboard%20design%20sprint%20continuous%20improvement%20testing%20refinement%20professional%20workspace%20emerald%20green%20accents%20modern%20aesthetic%20clean%20minimalist&width=800&height=500&seq=principle-iterative-process-emerald-new&orientation=landscape',
      position: 'bottom-right'
    },
  ];

  return (
    <section
      id="philosophy"
      ref={sectionRef}
      className="relative py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">My Approach</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Design Principles
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            The core values that guide every design decision I make
          </p>
        </div>

        {/* Principles Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {principles.map((principle, index) => (
            <div
              key={principle.title}
              className={`group bg-white border-2 border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${hoveredCard === principle.title ? 'pb-8' : ''}`}
              style={{ transitionDelay: `${index * 0.15}s` }}
              onMouseEnter={(e) => {
                setHoveredCard(principle.title);
                e.currentTarget.style.borderColor = '#6b3d52';
              }}
              onMouseLeave={(e) => {
                setHoveredCard(null);
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden bg-slate-100">
                <img
                  src={principle.image}
                  alt={principle.title}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Icon */}
                <div className="w-16 h-16 flex items-center justify-center rounded-xl mb-6" style={{ backgroundColor: '#6b3d52' }}>
                  <i className={`${principle.icon} text-3xl text-white`}></i>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{principle.title}</h3>

                {/* One-liner */}
                <p className="text-base text-slate-600 mb-4 font-medium italic">{principle.oneLiner}</p>

                {/* Dropdown Arrow Indicator */}
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <span>Hover to read more</span>
                  <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${hoveredCard === principle.title ? 'rotate-180' : ''}`}></i>
                </div>

                {/* Expandable Full Description */}
                <div
                  className={`transition-all duration-500 overflow-hidden ${
                    hoveredCard === principle.title ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-4 border-t-2 border-slate-200">
                    <p className="text-base text-slate-600 leading-relaxed">{principle.fullDesc}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
